/*
 * (c) Copyright, 2025, Medidata Solutions Worldwide, Inc. All rights reserved.
 */

import { useServiceContext } from '../root'
import { SEARCH_TYPE_V2 } from '../constants/search'
import type { FilterValues } from '../pages/search/types'

/**
 * Custom hook for search service logic
 * Handles filter assembly, pagination, and ElasticSearch feature detection
 */
export function useSearchService() {
  const context = useServiceContext()

  // Check ElasticSearch feature flag
  const isElasticSearchEnabled = context.service.init.getFeaturesInfo()?.allFeaturesData?.elasticSearchEnabled || false

  /**
   * Assemble filters from Formik form values to API parameters
   * @param values - Formik form values
   * @param isFromPageMenu - Whether called from page menu (affects updatedType inclusion)
   * @returns API parameters object
   */
  const assembleFiltersFromFormikValues = (values: FilterValues, isFromPageMenu?: boolean) => {
    const obj: any = {}

    // Handle date range
    if (values?.lastUpdatedType === 'CUSTOM' && values.updatedFrom && values.updatedTo) {
      const fromDate = values.updatedFrom instanceof Date ? values.updatedFrom : new Date(values.updatedFrom)
      const toDate = values.updatedTo instanceof Date ? values.updatedTo : new Date(values.updatedTo)

      // Validate that dates are valid
      if (!isNaN(fromDate.getTime()) && !isNaN(toDate.getTime())) {
        // Set hours and get timestamps - API expects 'updatedFrom' and 'updatedTo' parameters
        fromDate.setHours(0, 0, 0, 0)
        toDate.setHours(23, 59, 59, 999)
        obj.updatedFrom = fromDate.getTime()
        obj.updatedTo = toDate.getTime()
        obj.updatedType = 'CUSTOM'
      }
    } else if (values?.lastUpdatedType && values?.lastUpdatedType !== 'CUSTOM') {
      obj.updatedType = values.lastUpdatedType
    } else {
      obj.updatedType = null
    }

    // Handle search type -> fields mapping
    // Support both URL values (lowercase) and constant values (uppercase)
    const searchType = values?.searchType
    switch (searchType) {
      case 'all':
        obj.fields = [
          SEARCH_TYPE_V2.filename,
          ...(isElasticSearchEnabled ? [SEARCH_TYPE_V2.content] : []),
          SEARCH_TYPE_V2.docUID
        ]
        break
      case 'content':
      case SEARCH_TYPE_V2.content: // 'CONTENT'
        obj.fields = SEARCH_TYPE_V2.content
        break
      case 'filename':
      case SEARCH_TYPE_V2.filename: // 'FILE_NAME'
        obj.fields = SEARCH_TYPE_V2.filename
        break
      case 'docUID':
      case SEARCH_TYPE_V2.docUID: // 'DOC_UID'
        obj.fields = SEARCH_TYPE_V2.docUID
        break
      default:
        obj.fields = SEARCH_TYPE_V2.filename
        break
    }

    // Handle status filter - 'ALL' means no filter
    if (values?.state === 'ALL') {
      obj.status = null
    } else if (values?.state) {
      obj.status = values.state
    }

    // Handle item type filter - 'ALL' means no filter
    if (values?.itemType === 'ALL') {
      obj.itemType = null
    } else if (values?.itemType) {
      obj.itemType = values.itemType
    }

    // Handle document type filter
    if (values?.docType?.value) {
      obj.type = values.docType.value
    }

    // Convert numeric strings to numbers, keep other phrases as strings
    obj.phrase = /^\d+$/.test(values.phrase) ? parseFloat(values.phrase) : values.phrase

    return obj
  }

  /**
   * Calculate offset and limit based on URL params and pagination state
   * @param urlParams - URL query parameters
   * @param metaFilter - Pagination metadata
   * @returns Object with limit and offset
   */
  const getOffsetAndLimit = (
    urlParams: { show: string; searchType?: string },
    metaFilter: { pageSize: number; pageStart: number }
  ) => {
    if (urlParams.show === 'all') {
      // Preview mode: show limited results per section
      return {
        limit: urlParams.searchType === 'content' ? 5 : 10,
        offset: 0
      }
    } else {
      // Full table mode: use pagination
      return {
        limit: metaFilter.pageSize,
        offset: metaFilter.pageSize * metaFilter.pageStart
      }
    }
  }

  /**
   * Load saved filter from server by ID
   * @param filterId - Filter ID to load
   * @returns Parsed filter object or undefined on error
   */
  const loadSavedFilter = async (filterId: string | number) => {
    try {
      const data = await context.api.formFilters.getById(Number(filterId))
      // The filter field contains the serialized filter data
      const parsedFilter = typeof data.filter === 'string' ? JSON.parse(data.filter) : data.filter
      return parsedFilter
    } catch (err) {
      console.error('Failed loading filter from server:', err)
      return undefined
    }
  }

  return {
    assembleFiltersFromFormikValues,
    getOffsetAndLimit,
    loadSavedFilter,
    isElasticSearchEnabled
  }
}
